/**
 * Pure helper for restoring the "no agent available" recovery panel across a
 * page reload — factored out of page.tsx the same way lib/feedback-restore.ts
 * factors out the per-message feedback map, so the "which handoff (if any)
 * still needs the panel" rule is unit-tested without mounting the page.
 *
 * Why this exists at all (see the `handoffTimeoutOffer` doc comment on
 * Message, types/chat.ts): a `handoff_timeout_offer` marker is an OUTSTANDING
 * OFFER — a live, actionable form — not a historical receipt like
 * `contact_summary`/`support_request_submitted`. It must render as the SAME
 * HandoffTimeoutPanel page.tsx already mounts for a live timeout, not a
 * second inline copy of the form (a second mount path for the same stateful
 * form is a divergence bug waiting to happen). This helper resolves which
 * handoffId (if any) that panel should be mounted for on a restored
 * conversation.
 */
import { MessageRole, type Message } from '@/types/chat';

/**
 * The handoffId of the LAST restored timeout offer in the transcript, unless
 * it has been fulfilled — in which case there is nothing outstanding to
 * restore and this returns undefined. Only the LAST offer is ever
 * considered: once a later offer exists, an earlier one is definitionally
 * over (a new handoff has since started), so there's no reason to resurface
 * it.
 *
 * The last offer counts as fulfilled when a `supportRequestSubmitted`
 * message occurs LATER in the list (index-wise — a submission earlier in
 * the transcript can't fulfill an offer that hadn't happened yet) and either:
 *   (a) its `handoffId` matches the offer's, or
 *   (b) it carries NO `handoffId` at all.
 *
 * (b) is a defensive fallback for a marker persisted before the backend
 * started including `handoff_id` on `support_request_submitted` (see
 * api/support.py) — such a receipt can't be correlated to any specific
 * offer, so treating it as fulfilling the outstanding one is the
 * conservative choice: it fails toward "don't ask twice" rather than risk
 * re-presenting a form the user already completed.
 */
export function outstandingTimeoutOffer(
  messages: readonly Message[]
): { handoffId: string } | undefined {
  let lastOfferIndex = -1;
  let lastOfferHandoffId = '';

  messages.forEach((m, index) => {
    if (m.role === MessageRole.SYSTEM && m.handoffTimeoutOffer) {
      lastOfferIndex = index;
      lastOfferHandoffId = m.handoffTimeoutOffer.handoffId;
    }
  });

  if (lastOfferIndex === -1) return undefined;

  const fulfilled = messages
    .slice(lastOfferIndex + 1)
    .some(
      (m) =>
        m.role === MessageRole.SYSTEM &&
        m.supportRequestSubmitted &&
        (!m.supportRequestSubmitted.handoffId ||
          m.supportRequestSubmitted.handoffId === lastOfferHandoffId)
    );

  return fulfilled ? undefined : { handoffId: lastOfferHandoffId };
}
